import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useQuiz from '../hooks/useQuiz';
import useQuizComodines from '../hooks/useQuizComodines';
import useQuizTimer from '../hooks/useQuizTimer';
import { inicializarSonidos, preloadSounds, obtenerEstadoJuego } from '../utils/quizUtils';
import { handleClick, terminarConcurso, reiniciarConcursoCompleto as utilsReiniciarConcursoCompleto } from '../utils/quizComponentUtils';
import { inicializarSonidosYReferencias } from '../utils/quizSounds';
import QuizDisplay from './QuizDisplay';
import QuizControls from './QuizControls';
import QuizExplanation from './QuizExplanation';
import QuizGameEnd from './quiz/QuizGameEnd';
import OverlayLinkButton from './OverlayLinkButton';
import buildApiUrl from '../config/apiUrlHelper';
import buildSoundUrl from '../config/soundUrlHelper';
import { construirEstadoOverlay, enviarEstadoOverlay } from '../utils/quiz/overlaySync';

// Importar el servicio de chat de Twitch
import TwitchChatService from '../services/twitchChatService';

const normalizarDuracionPorPregunta = (valor) => {
  const numero = Number(valor);

  if (!Number.isFinite(numero) || numero <= 0) {
    return 0;
  }

  return Math.floor(numero);
};

const renderQuizAuroraShell = (content) => (
  <section className="relative min-h-[calc(100dvh-4rem)] overflow-hidden px-4 py-4 sm:px-6 sm:py-6">
    <div className="relative z-10">{content}</div>
  </section>
);

const Quiz = () => {
 const navigate = useNavigate();
  
  // Hooks para la lógica del quiz
  const {
    preguntas,
    preguntaActual,
    respuestaSeleccionada,
    respuestaRevelada,
    respuestas,
    puntuacion,
    juegoTerminado,
    respuestasMezcladasPreguntaActual,
    respuestasMezcladas, // Añadir la variable para el array completo de respuestas mezcladas
    respuestasEliminadas,
    respuestasVisibles,
    duracionConcurso,
    tiempoRestante,
    temporizadorActivo,
    tiempoPorPregunta,
    primeraCarga,
    indiceRespuestaCorrecta,
    cargarPreguntas,
    manejarSeleccionRespuesta,
    resolverPregunta,
    siguientePregunta,
    reiniciarConcurso,
    setPreguntaActual,
    setRespuestaSeleccionada,
    setRespuestaRevelada,
    setRespuestas,
    setPreguntas,
    setRespuestasMezcladas,
    setPuntuacion,
    setJuegoTerminado,
    setRespuestasEliminadas,
    setRespuestasVisibles,
    setDuracionConcurso,
    setTiempoRestante,
    setTemporizadorActivo,
    setTiempoPorPregunta,
    aciertoSoundRef,
    errorSoundRef,
    tensionSoundRef,
    tensionSoundFadeIn, // Importar la función para que pueda usarse en el componente
    tensionSoundFadeOut, // Importar la función para que pueda usarse en el componente
    iniciarConcurso,
    fallos,
    setFallos
  } = useQuiz();

  // Hook para comodines
  const {
    comodines,
    tiempoLlamada,
    temporizadorLlamadaActivo,
    mostrarTemporizadorLlamada,
    mostrarGraficoPublico,
    datosGraficoPublico,
    tiempoRestantePublico,
    temporizadorPublicoActivo,
    votosPublico,
    usarComodin,
    resetComodines,
    setComodines,
    setTiempoLlamada,
    setTemporizadorLlamadaActivo,
    setMostrarTemporizadorLlamada,
    setMostrarGraficoPublico,
    setDatosGraficoPublico,
    setTiempoRestantePublico,
    setTemporizadorPublicoActivo,
    setVotosPublico
  } = useQuizComodines();

  // Hook para temporizador
  const { tiempoRestante: tiempoRestanteTimer, reiniciarTemporizador } = useQuizTimer(
    tiempoPorPregunta,
    temporizadorActivo,
    () => {
      // Callback cuando se agota el tiempo
      if (respuestaRevelada || respuestas[preguntaActual] !== null) {
        return;
      }

      const indicesIncorrectos = respuestasMezcladasPreguntaActual
        .map((respuesta, indice) => (respuesta.esCorrecta ? null : indice))
        .filter(indice => indice !== null);

      if (indicesIncorrectos.length > 0) {
        const indiceIncorrectoAleatorio = indicesIncorrectos[Math.floor(Math.random() * indicesIncorrectos.length)];
        resolverPregunta(indiceIncorrectoAleatorio);
        return;
      }

      if (respuestaSeleccionada === null) {
        setRespuestaSeleccionada(-1);
      }
      setRespuestaRevelada(true);
    },
    setTemporizadorActivo,
    preguntaActual
  );

  // Referencia para el estado de juego
  const juegoRef = useRef();

  // Inicializar el servicio de chat de Twitch
  const twitchChatService = useRef(null);
  // Periodo de gracia del comodín del público: tras acabar el cronómetro, el
  // chat sigue contando votos 5s más antes de detener el conteo.
  const graciaPublicoRef = useRef(null);
  useEffect(() => {
    twitchChatService.current = new TwitchChatService('don_diegons');
    twitchChatService.current.initialize();
  }, []);

  // Estados para el historial de votos
  const [historialVotos, setHistorialVotos] = React.useState([]);

  // Visibilidad del modal de soborno (reflejada en el overlay de OBS en tiempo real).
  // Declarado aquí (antes del efecto que empuja el estado) para evitar TDZ.
  const [sobornoVisibleOverlay, setSobornoVisibleOverlay] = React.useState(false);

  // Efecto para manejar el temporizador del comodín de la llamada
  useEffect(() => {
    let interval = null;
    if (temporizadorLlamadaActivo && tiempoLlamada > 0) {
      interval = setInterval(() => {
        setTiempoLlamada(tiempo => {
          if (tiempo <= 1) {
            clearInterval(interval);
            setTemporizadorLlamadaActivo(false);
            return 0;
          }
          return tiempo - 1;
        });
      }, 1000);
    } else if (tiempoLlamada <= 0) {
      setTemporizadorLlamadaActivo(false);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [temporizadorLlamadaActivo, tiempoLlamada]);

  // Efecto para manejar el temporizador del comodín del público
  useEffect(() => {
    // Este efecto solo debería ejecutarse cuando se activa o desactiva el comodín del público
    if (temporizadorPublicoActivo && preguntaActual !== undefined && respuestasMezcladasPreguntaActual.length > 0) {
      // Inicializar los datos del gráfico con valores iniciales
      const respuestas = respuestasMezcladasPreguntaActual || [];
      if (respuestas.length > 0) {
        // Inicializar con 0 puntos para cada opción
        const datosIniciales = {
          labels: respuestas.map((r, i) => `${r.texto} (0 votos)`), // A, B, C, D con 0 votos iniciales
          datasets: [{
            label: 'Votos del público (%)',
            data: [0, 0, 0], // Datos iniciales en 0 para cada opción
            backgroundColor: [
              'rgba(25, 99, 132, 0.6)',
              'rgba(54, 162, 235, 0.6)',
              'rgba(25, 205, 86, 0.6)',
              'rgba(75, 192, 192, 0.6)'
            ],
            borderWidth: 1
          }]
        };
        setDatosGraficoPublico(datosIniciales);
        // Actualizar también el estado de votos público con 0 para cada opción
        setVotosPublico([0, 0, 0, 0]);
      }

      // Iniciar el conteo de votos del chat de Twitch cuando se active el comodín
      if (twitchChatService.current) {
        // Cancelar cualquier periodo de gracia pendiente de una activación previa
        // (evita que un stop retardado corte esta nueva ronda).
        if (graciaPublicoRef.current) {
          clearTimeout(graciaPublicoRef.current);
          graciaPublicoRef.current = null;
        }
        // Iniciar el modo de conteo primero
        twitchChatService.current.startPolling(); // Usar el nuevo método para iniciar el conteo

        // Establecer las opciones de respuesta actuales para que el servicio pueda detectar coincidencias
        const opcionesActuales = respuestasMezcladasPreguntaActual?.map(r => r.texto) || [];
        twitchChatService.current.setOpcionesRespuesta(opcionesActuales);

        // Establecer votos iniciales de 0 puntos por opción para que comiencen vacíos
        const votosIniciales = { A: 0, B: 0, C: 0, D: 0 };
        twitchChatService.current.setInitialVotes(votosIniciales);
      }

      // Función para actualizar el gráfico con los votos reales
      const actualizarGrafico = (voteData) => {
        const respuestas = respuestasMezcladasPreguntaActual || [];
        if (respuestas.length > 0) {
          // Mapear los votos del servicio a la estructura que necesita el gráfico
          const nuevosVotos = [voteData.counts.A, voteData.counts.B, voteData.counts.C, voteData.counts.D];
          setVotosPublico(nuevosVotos);
          
          // Actualizar el historial de votos con la información actual
          const nuevoRegistroHistorial = {
            timestamp: Date.now(),
            counts: {...voteData.counts},
            percentages: {...voteData.percentages},
            total: voteData.total
          };
          setHistorialVotos(prevHistorial => [...prevHistorial, nuevoRegistroHistorial]);
          
          const datosActualizados = {
            labels: respuestas.map((r, i) => `${r.texto} (${nuevosVotos[i]} votos)`), // Mostrar texto completo de la opción con número de votos
            datasets: [{
              label: 'Votos del público (%)',
              data: [voteData.percentages.A, voteData.percentages.B, voteData.percentages.C, voteData.percentages.D], // Porcentajes reales basados en votos contados
              backgroundColor: [
                'rgba(255, 99, 132, 0.6)',
                'rgba(54, 162, 235, 0.6)',
                'rgba(25, 205, 86, 0.6)',
                'rgba(75, 192, 192, 0.6)'
              ],
              borderWidth: 1
            }]
          };
          setDatosGraficoPublico(datosActualizados);
        }
      };

      // Añadir callback para recibir actualizaciones de votos en tiempo real
      if (twitchChatService.current) {
        // Añadir callback sin reiniciar los votos para acumular votos entre diferentes activaciones
        twitchChatService.current.addVoteCallback(actualizarGrafico);
      }
    }

    // Efecto separado para manejar solo el temporizador
    let interval = null;
    if (temporizadorPublicoActivo && tiempoRestantePublico > 0) {
      interval = setInterval(() => {
        setTiempoRestantePublico(tiempo => {
          if (tiempo <= 1) {
            clearInterval(interval);
            setTemporizadorPublicoActivo(false);
            // El cronómetro llega a 0, pero el chat sigue contando votos 5s más
            // antes de detener el conteo (periodo de gracia).
            if (twitchChatService.current) {
              const servicio = twitchChatService.current;
              if (graciaPublicoRef.current) clearTimeout(graciaPublicoRef.current);
              graciaPublicoRef.current = setTimeout(() => {
                servicio.stopPolling();
                graciaPublicoRef.current = null;
              }, 5000);
            }
            return 0;
          }
          if (tiempo === 22) {
            // Reproducir sonido cuando quedan 22 segundos
            const audio = new Audio(buildSoundUrl('/sounds/lyoi20s.mp3'));
            audio.preload = 'auto';
            audio.load();
            audio.play().catch(e => console.log("Error al reproducir sonido:", e));
          }
          return tiempo - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
      // Asegurar que se detenga el conteo al desmontar el efecto
      if (twitchChatService.current && !temporizadorPublicoActivo) {
        twitchChatService.current.stopPolling(); // Detener el conteo al desmontar
      }
    };
  }, [temporizadorPublicoActivo, preguntaActual]); // Eliminé tiempoRestantePublico de las dependencias para evitar reinicios

    // Iniciar concurso al montar el componente
  useEffect(() => {
    // Inicializar sonidos primero
    const soundPaths = {
      acierto: '/sounds/acierto-sound.mp3',
      error: '/sounds/error-sound.mp3',
      tension: '/sounds/Gameshow Background Tension.mp3'
    };

    const soundSettings = {
      acierto: { volume: 0.7 },
      error: { volume: 0.7 },
      tension: { volume: 0.3, loop: true }
    };

    inicializarSonidosYReferencias(soundPaths, soundSettings, aciertoSoundRef, errorSoundRef, tensionSoundRef);

    // Usar iniciarConcurso para cargar preguntas y reproducir música
    iniciarConcurso(); // Carga las preguntas y reproduce la música de tensión
  }, []);

 // Cargar la duración del concurso desde sessionStorage
  useEffect(() => {
    const duracionGuardada = sessionStorage.getItem('duracionConcurso');
    const duracion = normalizarDuracionPorPregunta(duracionGuardada);

    setDuracionConcurso(duracion);
    setTiempoPorPregunta(duracion);
    setTiempoRestante(duracion);

    if (duracion === 0) {
      setTemporizadorActivo(false);
    }
  }, []);

  useEffect(() => {
    if (tiempoPorPregunta <= 0) {
      reiniciarTemporizador(0, false);
      setTemporizadorActivo(false);
      return;
    }

    // Verificacion activa: cada pregunta debe arrancar con el mismo tiempo configurado.
    reiniciarTemporizador(tiempoPorPregunta, false);
    setTemporizadorActivo(false);
  }, [preguntaActual, tiempoPorPregunta, reiniciarTemporizador, setTemporizadorActivo]);

  useEffect(() => {
    if (tiempoPorPregunta > 0 && tiempoRestanteTimer > tiempoPorPregunta) {
      console.warn('[Quiz] Se detecto deriva de temporizador y se corrigio.', {
        tiempoPorPregunta,
        tiempoRestanteTimer,
        preguntaActual
      });
      reiniciarTemporizador(tiempoPorPregunta, false);
    }
  }, [tiempoPorPregunta, tiempoRestanteTimer, preguntaActual, reiniciarTemporizador]);

  useEffect(() => {
    setTiempoRestante(tiempoRestanteTimer);
  }, [tiempoRestanteTimer, setTiempoRestante]);



  // Compartir estado del concurso en localStorage
 useEffect(() => {
    const estadoConcurso = obtenerEstadoJuego({
      preguntaActual,
      respuestaSeleccionada,
      respuestaRevelada,
      respuestas,
      puntuacion,
      juegoTerminado,
      respuestasMezcladas: [],
      respuestasEliminadas,
      respuestasVisibles,
      comodines,
      tiempoRestante,
      tiempoLlamada,
      temporizadorLlamadaActivo,
      mostrarTemporizadorLlamada,
      mostrarGraficoPublico,
      datosGraficoPublico,
      tiempoRestantePublico,
      temporizadorPublicoActivo,
      votosPublico,
      preguntas,
      indiceRespuestaCorrecta,
      duracionConcurso
    });

    localStorage.setItem('estadoConcurso', JSON.stringify(estadoConcurso));

    // Empujar el estado al servidor para el overlay de OBS (tiempo real, otro navegador).
    enviarEstadoOverlay(construirEstadoOverlay({
      preguntas,
      preguntaActual,
      respuestas,
      respuestasMezcladas,
      respuestasMezcladasPreguntaActual,
      respuestasVisibles,
      respuestasEliminadas,
      respuestaSeleccionada,
      respuestaRevelada,
      indiceRespuestaCorrecta,
      puntuacion,
      juegoTerminado,
      comodines,
      tiempoRestante,
      duracionConcurso,
      tiempoLlamada,
      temporizadorLlamadaActivo,
      mostrarTemporizadorLlamada,
      mostrarGraficoPublico,
      datosGraficoPublico,
      tiempoRestantePublico,
      temporizadorPublicoActivo,
      votosPublico,
      sobornoVisible: sobornoVisibleOverlay,
    }));
  }, [
    preguntaActual, respuestaSeleccionada, respuestaRevelada, respuestas, puntuacion,
    juegoTerminado, respuestasEliminadas, respuestasVisibles, comodines, tiempoRestante,
    tiempoLlamada, temporizadorLlamadaActivo, mostrarTemporizadorLlamada, mostrarGraficoPublico,
    datosGraficoPublico, tiempoRestantePublico, temporizadorPublicoActivo, votosPublico,
    preguntas, indiceRespuestaCorrecta, duracionConcurso, respuestasMezcladas,
    respuestasMezcladasPreguntaActual, sobornoVisibleOverlay
 ]);

  // Manejar efectos del temporizador
  useEffect(() => {
    if (tiempoPorPregunta > 0 && respuestasVisibles === 4 && !temporizadorActivo && !respuestaRevelada) {
      setTemporizadorActivo(true);
      if (tensionSoundRef.current) {
        tensionSoundRef.current.pause();
        tensionSoundRef.current.currentTime = 0;
      }
    } else if (respuestasVisibles < 4 && tiempoPorPregunta > 0 && !respuestaRevelada && !temporizadorActivo) {
      setTemporizadorActivo(false);
      if (tensionSoundRef.current) {
        tensionSoundRef.current.currentTime = 0;
        tensionSoundRef.current.loop = true;
        tensionSoundRef.current.play().catch(e => console.log("Error al reproducir sonido de tensión:", e));
      }
    }
 }, [respuestasVisibles, tiempoPorPregunta, temporizadorActivo, respuestaRevelada]);

  // Reiniciar visibilidad de respuestas al cambiar de pregunta
 useEffect(() => {
    setRespuestasVisibles(0);
  }, [preguntaActual]);

  // Función para manejar el clic en cualquier parte de la página
  useEffect(() => {
    const handler = handleClick(respuestaRevelada, respuestasVisibles, setRespuestasVisibles);

    document.addEventListener('click', handler);
    return () => {
      document.removeEventListener('click', handler);
    };
  }, [respuestaRevelada, respuestasVisibles]);

  // Estado para el diálogo de confirmación
  const [mostrarDialogoConfirmacion, setMostrarDialogoConfirmacion] = React.useState(false);

  // Función para reiniciar el concurso
  const reiniciarConcursoCompleto = () => {
    utilsReiniciarConcursoCompleto(reiniciarConcurso, resetComodines, setRespuestasVisibles, setFallos);
  };

    // Función para terminar el concurso
  const handleTerminarConcurso = async () => {
    const cantidadMostrada = Math.max(0, Math.min(preguntas.length, preguntaActual + 1));
    const preguntasMostradasIds = preguntas
      .slice(0, cantidadMostrada)
      .map((p) => Number(p?.id))
      .filter((id) => Number.isInteger(id) && id > 0)
      .filter((id, index, arr) => arr.indexOf(id) === index);

    await terminarConcurso(setJuegoTerminado, preguntasMostradasIds);
  };

  // Guardia de inactividad global: sin importar la duración configurada por
  // pregunta (incluso "sin límite"), si pasa 1 hora real sin resolver la
  // pregunta actual, se finaliza todo el concurso automáticamente.
  useEffect(() => {
    if (juegoTerminado || respuestaRevelada) {
      return undefined;
    }

    const INACTIVIDAD_MAXIMA_MS = 60 * 60 * 1000;
    const timeoutId = setTimeout(() => {
      console.warn('[Quiz] Concurso finalizado por inactividad: 1 hora sin resolver la pregunta actual.');
      handleTerminarConcurso();
    }, INACTIVIDAD_MAXIMA_MS);

    return () => clearTimeout(timeoutId);
  }, [preguntaActual, respuestaRevelada, juegoTerminado]);

  // Si no hay preguntas, mostrar mensaje de carga
 if (preguntas.length === 0) {
    return renderQuizAuroraShell(
      <div className="container mx-auto p-6 text-center">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-white">Cargando concurso...</h2>
      </div>
    );
  }


  // Pregunta actual
  const pregunta = preguntas[preguntaActual];
  if (!pregunta) {
    return renderQuizAuroraShell(
      <div className="container mx-auto p-6 text-center text-gray-800 dark:text-white transition-colors duration-300">
        <h2 className="text-2xl font-bold">Error al cargar la pregunta</h2>
      </div>
    );
  }

  // Función para cambiar la pregunta actual por otra de la misma dificultad usando el backend
  const cambiarPreguntaMago = async () => {
    try {
      if (!pregunta || !pregunta.id) {
        throw new Error('No hay una pregunta válida para reemplazar');
      }

      const dificultad = pregunta.dificultad;
      const preguntasVistas = preguntas.map(p => p.id).filter(Boolean);

      const concursoConfigRaw = sessionStorage.getItem('concursoConfig') || sessionStorage.getItem('previewConfig');
      const concursoConfig = concursoConfigRaw ? JSON.parse(concursoConfigRaw) : null;

      const response = await fetch(buildApiUrl('cambiar_pregunta_mago.php'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dificultad,
          pregunta_actual_id: pregunta.id,
          pregunta_actual_texto: pregunta.pregunta,
          preguntas_vistas: preguntasVistas,
          categorias: Array.isArray(concursoConfig?.categorias) ? concursoConfig.categorias : [],
          etiquetas: Array.isArray(concursoConfig?.etiquetas) ? concursoConfig.etiquetas : []
        })
      });

      const data = await response.json();

      if (!response.ok || !data || !data.pregunta || !data.pregunta.id) {
        setComodines(prev => ({ ...prev, mago: Math.min(prev.mago + 1, 1) }));
        throw new Error(data?.error || 'No se pudo cambiar la pregunta');
      }

      if (Number(data.pregunta.id) === Number(pregunta.id)) {
        setComodines(prev => ({ ...prev, mago: Math.min(prev.mago + 1, 1) }));
        throw new Error('El mago devolvió la misma pregunta, operación cancelada');
      }

      setPreguntas(prevPreguntas => {
        const nuevasPreguntas = [...prevPreguntas];
        nuevasPreguntas[preguntaActual] = data.pregunta;
        sessionStorage.setItem('preguntasConcurso', JSON.stringify(nuevasPreguntas));
        return nuevasPreguntas;
      });

      setRespuestas(prevRespuestas => {
        const nuevasRespuestas = [...prevRespuestas];
        nuevasRespuestas[preguntaActual] = null;
        return nuevasRespuestas;
      });

      setRespuestasMezcladas(prevMezcladas => {
        const nuevasMezcladas = [...prevMezcladas];
        nuevasMezcladas[preguntaActual] = undefined;
        return nuevasMezcladas;
      });

      setRespuestasEliminadas(prevEliminadas => {
        const nuevasEliminadas = Array.isArray(prevEliminadas) ? [...prevEliminadas] : [];
        nuevasEliminadas[preguntaActual] = [];
        return nuevasEliminadas;
      });

      setRespuestaSeleccionada(null);
      setRespuestaRevelada(false);
      setRespuestasVisibles(0);
      setMostrarGraficoPublico(false);
      setTemporizadorPublicoActivo(false);
      setTiempoRestantePublico(30);
      setVotosPublico([0, 0, 0, 0]);
      setHistorialVotos([]);

      if (twitchChatService.current) {
        twitchChatService.current.stopPolling();
      }
    } catch (e) {
      alert('Error al cambiar la pregunta: ' + e.message);
    }
  };

  return renderQuizAuroraShell(
    <div className="container mx-auto p-4 dark:text-white transition-colors duration-300">
      <OverlayLinkButton />
      <QuizGameEnd
        puntuacion={puntuacion}
        preguntas={preguntas}
        juegoTerminado={juegoTerminado}
        tensionSoundRef={tensionSoundRef}
        tensionSoundFadeOut={tensionSoundFadeOut}
        reiniciarConcursoCompleto={reiniciarConcursoCompleto}
      />
      <div className="flex flex-col lg:flex-row gap-6 max-w-7xl mx-auto">
        {/* Componente de visualización del quiz */}
        <QuizDisplay
          preguntaActual={preguntaActual}
          preguntas={preguntas}
          pregunta={pregunta}
          puntuacion={puntuacion}
          tiempoRestante={tiempoRestanteTimer}
          duracionConcurso={duracionConcurso}
          respuestasVisibles={respuestasVisibles}
          respuestasEliminadas={respuestasEliminadas}
          respuestas={respuestas}
          respuestasMezcladasPreguntaActual={respuestasMezcladasPreguntaActual}
          respuestasMezcladas={respuestasMezcladas} // Pasar el array completo de respuestas mezcladas
          indiceRespuestaCorrecta={indiceRespuestaCorrecta}
          mostrarDialogoConfirmacion={mostrarDialogoConfirmacion}
          setMostrarDialogoConfirmacion={setMostrarDialogoConfirmacion}
          resolverPregunta={resolverPregunta}
          siguientePregunta={siguientePregunta}
          reiniciarConcurso={reiniciarConcursoCompleto}
          respuestaSeleccionada={respuestaSeleccionada}
          respuestaRevelada={respuestaRevelada}
          manejarSeleccionRespuesta={manejarSeleccionRespuesta}
          setRespuestaSeleccionada={setRespuestaSeleccionada}
        />
        
        {/* Componente de controles del quiz */}
        <QuizControls
          comodines={comodines}
          mostrarTemporizadorLlamada={mostrarTemporizadorLlamada}
          setMostrarTemporizadorLlamada={setMostrarTemporizadorLlamada}
          tiempoLlamada={tiempoLlamada}
          temporizadorLlamadaActivo={temporizadorLlamadaActivo}
          setTemporizadorLlamadaActivo={setTemporizadorLlamadaActivo}
          setTiempoLlamada={setTiempoLlamada}
          mostrarGraficoPublico={mostrarGraficoPublico}
          datosGraficoPublico={datosGraficoPublico}
          tiempoRestantePublico={tiempoRestantePublico}
          setTiempoRestantePublico={setTiempoRestantePublico}
          votosPublico={votosPublico}
          setVotosPublico={setVotosPublico}
          respuestasMezcladasPreguntaActual={respuestasMezcladasPreguntaActual}
          usarComodin={(
            tipo,
            respuestasVisiblesArg,
            respuestaReveladaArg,
            preguntaActualArg,
            respuestasArg,
            respuestasMezcladasArg,
            setRespuestasVisiblesArg,
            setRespuestasEliminadasArg,
            forzar = false,
            extra = {}
          ) => {
            return usarComodin(
              tipo,
              respuestasVisiblesArg,
              respuestaReveladaArg,
              preguntaActualArg,
              respuestasArg,
              respuestasMezcladasArg,
              setRespuestasVisiblesArg,
              setRespuestasEliminadasArg,
              forzar,
              extra
            );
          }}
          preguntaActual={preguntaActual}
          respuestas={respuestas}
          respuestaRevelada={respuestaRevelada}
          respuestasVisibles={respuestasVisibles}
          setRespuestasVisibles={setRespuestasVisibles}
          setRespuestasEliminadas={setRespuestasEliminadas}
          setMostrarGraficoPublico={setMostrarGraficoPublico}
          setTemporizadorPublicoActivo={setTemporizadorPublicoActivo}
          twitchChatService={twitchChatService}
          historialVotos={historialVotos}
          setHistorialVotos={setHistorialVotos}
          pregunta={pregunta} // Pasar la pregunta para mostrar la explicación
          preguntasAcertadas={puntuacion} // NUEVO: pasar aciertos
          cambiarPreguntaMago={cambiarPreguntaMago}
          mostrarDialogoConfirmacion={mostrarDialogoConfirmacion}
          setMostrarDialogoConfirmacion={setMostrarDialogoConfirmacion}
          onConfirmTerminar={handleTerminarConcurso}
          puntuacion={puntuacion}
          totalPreguntas={preguntas.length}
          fallos={fallos}
          onSobornoVisibleChange={setSobornoVisibleOverlay}
        />
      </div>
    </div>
  );
};

export default Quiz;
